import { toast } from "sonner";
import { API_ENDPOINTS, HTTP_METHODS } from "../config/api";
import { BaseApiService, ApiResponse } from "./baseApi";

// Heat Number Consumption Report DTO
export interface HeatNumberConsumptionReportDto {
  heatNo?: string;
  rawItemId?: number;
  itemName?: string;
  rawItemSku?: string;
  rawItemCategoryId?: number;
  branchId?: number;
  poQuantity?: number;
  receivedQuantity?: number;
  totalChallanInbound?: number;
  originalQuantity?: number;
  totalConsumed?: number;
  totalChallanOutbound?: number;
  rejectQuantity?: number;
  remainingQuantity?: number;
  issueCount?: number;
  lastActivityDate?: string; // LocalDateTime from backend
  grnId?: number;
  grnNumber?: string;
  grnDate?: string; // LocalDate from backend
  supplierName?: string;
  unitPrice?: number;
  lineTotal?: number;
  nop?: number; // Number of Pieces
  grade?: string;
  remainingNop?: number; // Remaining Number of Pieces
  supplierHeatNo?: string;
  tcNo?: string;
  tcDocumentUploaded?: boolean;
  dcNo?: string;
}

// Material Issue Report DTO - matches actual API response
export interface MaterialIssueReportDto {
  id?: number;
  workOrderId?: number | null;
  workOrderOperationId?: number | null;
  rawItemId?: number;
  rawCategoryId?: number;
  endgoodId?: number | null;
  issueQuantity?: number;
  consumedQuantity?: number;
  returnedQuantity?: number | null;
  wastedQuantity?: number | null;
  issueType?: string;
  issueStatus?: string;
  issueDate?: string; // "2025-04-11 05:30:00" format
  consumptionDate?: string;
  returnDate?: string | null;
  issuedBy?: string;
  receivedBy?: string | null;
  storeLocation?: string | null;
  batchNumber?: string | null;
  lotNumber?: string | null;
  expiryDate?: string | null;
  unitCost?: number | null;
  totalCost?: number | null;
  notes?: string | null;
  issueReason?: string | null;
  wastageReason?: string | null;
  rackNumber?: string | null;
  heatNumber?: string;
  qualityRemarks?: string | null;
  isActive?: boolean;
  branchId?: number;
  createdBy?: number;
  updatedBy?: number | null;
  createdAt?: string;
  updatedAt?: string | null;
  workOrderNumber?: string | null;
  rawItemName?: string;
  rawItemSku?: string;
  rawCategoryName?: string;
  endgoodName?: string | null;
  workOrderEndgoodName?: string | null;
  endgoodSku?: string | null;
  workOrderOperationName?: string | null;
  branchName?: string;
  branchCode?: string;
}

// Material Issue Report Request DTO
export interface MaterialIssueReportRequestDto {
  startDate?: string; // Format: "YYYY-MM-DD"
  endDate?: string; // Format: "YYYY-MM-DD"
}

// Inventory Aging Analysis DTO
export interface InventoryAgingAnalysisDto {
  stock0To30Days?: number;
  stock31To60Days?: number;
  stock61To90Days?: number;
  stock91To120Days?: number;
  stockOver120Days?: number;
  totalStock?: number;
  value0To30Days?: number;
  value31To60Days?: number;
  value61To90Days?: number;
  value91To120Days?: number;
  valueOver120Days?: number;
  totalValue?: number;
}

// Inventory Report API Service
class InventoryReportApiService extends BaseApiService {
  
  // Get heat number consumption report by branch
  async getHeatNumberConsumptionReportByBranch(
    branchId: number
  ): Promise<ApiResponse<HeatNumberConsumptionReportDto[]>> {
    try {
      // Backend returns List directly, not wrapped in ApiResponse
      const data = await this.makeRequest<HeatNumberConsumptionReportDto[]>(
        API_ENDPOINTS.INVENTORY_REPORTS.HEAT_NUMBER_CONSUMPTION(branchId),
        { method: HTTP_METHODS.GET }
      );
      
      // Wrap in ApiResponse format for consistency
      return {
        success: true,
        message: 'Report fetched successfully',
        data: Array.isArray(data) ? data : []
      };
    } catch (error) {
      // console.error('InventoryReportApiService: Error fetching heat number consumption report:', error);
      toast.error('Failed to fetch heat number consumption report');
      throw error;
    }
  }

  // Get material issue report by branch with optional date range
  async getMaterialIssueReportByBranch(
    branchId: number,
    startDate?: string,
    endDate?: string
  ): Promise<ApiResponse<MaterialIssueReportDto[]>> {
    try {
      // Prepare request body - always send POST, with date range if provided
      const requestBody: MaterialIssueReportRequestDto = 
        (startDate && endDate) ? { startDate, endDate } : {};
      
      // Backend returns List directly, not wrapped in ApiResponse
      const data = await this.makeRequest<MaterialIssueReportDto[]>(
        API_ENDPOINTS.INVENTORY_REPORTS.MATERIAL_ISSUE(branchId),
        { 
          method: HTTP_METHODS.POST,
          body: JSON.stringify(requestBody),
          headers: {
            'Content-Type': 'application/json'
          }
        }
      );
      
      // Wrap in ApiResponse format for consistency
      return {
        success: true,
        message: 'Material issue report fetched successfully',
        data: Array.isArray(data) ? data : []
      };
    } catch (error) {
      // console.error('InventoryReportApiService: Error fetching material issue report:', error);
      toast.error('Failed to fetch material issue report');
      throw error;
    }
  }

  // Get inventory as of date
  async getInventoryAsOfDate(
    targetDate: string,
    branchId?: number,
    categoryName?: string
  ): Promise<ApiResponse<HeatNumberConsumptionReportDto[]>> {
    try {
      const params = new URLSearchParams();
      params.append('targetDate', targetDate);
      if (branchId) params.append('branchId', branchId.toString());
      if (categoryName) params.append('categoryName', categoryName);
      
      // Backend returns List directly, not wrapped in ApiResponse
      const data = await this.makeRequest<HeatNumberConsumptionReportDto[]>(
        `${API_ENDPOINTS.INVENTORY_REPORTS.INVENTORY_AS_OF_DATE()}?${params.toString()}`,
        { method: HTTP_METHODS.GET }
      );
      
      // Wrap in ApiResponse format for consistency
      return {
        success: true,
        message: 'Inventory as of date fetched successfully',
        data: Array.isArray(data) ? data : []
      };
    } catch (error) {
      // console.error('InventoryReportApiService: Error fetching inventory as of date:', error);
      toast.error('Failed to fetch inventory as of date');
      throw error;
    }
  }

  // Get inventory aging analysis
  async getInventoryAgingAnalysis(
    branchId?: number
  ): Promise<ApiResponse<InventoryAgingAnalysisDto>> {
    try {
      const data = await this.makeRequest<InventoryAgingAnalysisDto>(
        API_ENDPOINTS.INVENTORY_REPORTS.AGING_ANALYSIS(branchId),
        { method: HTTP_METHODS.GET }
      );
      
      return {
        success: true,
        message: 'Inventory aging analysis fetched successfully',
        data: data
      };
    } catch (error) {
      // console.error('InventoryReportApiService: Error fetching inventory aging analysis:', error);
      toast.error('Failed to fetch inventory aging analysis');
      throw error;
    }
  }
}

export const inventoryReportApiService = new InventoryReportApiService();

